import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import type { DataViewOperation } from '@adornis/data-view/data-view-operation.js';
import { View } from '@adornis/data-view/db/view.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { html, nothing } from 'lit';
import { format, intervalToDuration } from 'date-fns';
import { EMPTY, catchError, exhaustMap, filter, from, of, timeout, timer } from 'rxjs';
import { CallSession } from '../../db/CallSession.js';
import { listCallOverview } from '../../db/call-origin-resolvers.js';
import { CallResultBadge } from './CallResultBadge.js';
import { CallSuccessSelect, CallSuccessSummary, createCallSuccessStore } from './CallSuccessReview.js';
import { SUCCESS_PERIODS, type SuccessPeriod } from './call-success-store.js';

/** How often an open /calls page re-reads the list. */
export const CALL_OVERVIEW_REFRESH_MS = 15000;
/** A request that hangs longer than this is given up, so the next refresh can run. */
const CALL_OVERVIEW_REQUEST_TIMEOUT_MS = 60000;

export type CallOverviewQuery = (
  skip: number,
  limit: number,
  filter: AdornisFilter,
) => (selection: BaseQLSelectionSet<CallSession>) => Promise<CallSession[]>;

const pageIsHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Hotfix 16/09/2026 - the DataTable operation for /calls: a bounded query, repeated on a timer.
 *
 * It is handed to the DataTable as an Observable on purpose. For a Promise the DataTable (usePagination) keeps the
 * old rows and only appends the tail of a new answer while fewer rows exist than it asked for, so a refreshed short
 * list would duplicate rows and never show a new call. An Observable answer replaces the rows as a whole.
 * One request at a time per table (exhaustMap), none while the tab is hidden, and a failed request waits for the
 * next tick instead of ending the refresh.
 */
export function pollCallOverview(
  query: CallOverviewQuery,
  refreshMs: number = CALL_OVERVIEW_REFRESH_MS,
): DataViewOperation<CallSession, [AdornisFilter]> {
  return (skip, limit, adornisFilter) => selection => {
    // The DataTable probes every operation with limit 0 just to see whether it is an Observable.
    if (!(limit > 0)) return of([]);
    return timer(0, refreshMs).pipe(
      filter(tick => tick === 0 || !pageIsHidden()),
      exhaustMap(() =>
        from(Promise.resolve().then(() => query(skip, limit, adornisFilter)(selection))).pipe(
          timeout(CALL_OVERVIEW_REQUEST_TIMEOUT_MS),
          catchError(err => {
            console.warn('CallOverview: refresh failed', err);
            return EMPTY;
          }),
        ),
      ),
    );
  };
}

const formatTime = (value: Date | null | undefined) => (value ? format(new Date(value), 'dd.MM.yyyy HH:mm:ss') : '-');

/**
 * Overview list of past calls using DataTable.
 * Bounded query over call_sessions (+ call-recordings for calls without a session row), re-read every 15 s
 * while the tab is visible and on "Aktualisieren". It never watches call-logs and never counts logs per row.
 */
export const CallOverview = stateful(() => {
  const [searchQuery, setSearchQuery] = useState('');
  // A new operation makes the DataTable start polling again at once; the rows on screen stay until the answer.
  const [reloadCount, setReloadCount] = useState(0);
  const [period, setPeriod] = useState<SuccessPeriod>('all');
  const successStore = useMemo(() => [createCallSuccessStore()], []);
  const operation = useMemo(
    () => [pollCallOverview((skip, limit, filter) => listCallOverview(skip, limit, filter, period))],
    [reloadCount, period],
  );

  // Build DataViewOptions even though we bypass server-side filtering – DataTable internally relies on options column definitions
  const dataViewOptions = buildFullOptions({
    class: CallSession,
    operation,
    isSortable: false,
    selectionSet: {
      _id: 1,
      sessionID: 1,
      startedAt: 1,
      endedAt: 1,
      callOrigin: 1,
      carrierPhoneNumber: 1,
      spokenPhoneNumber: 1,
    },
    customColumns: [
      {
        fieldName: 'sessionID',
        columnName: 'Call ID',
        columnWidth: '180px',
      },
      {
        fieldName: 'startedAt',
        columnName: 'Start',
        columnWidth: '160px',
        renderItem: (s: CallSession) => formatTime(s.startedAt),
      },
      {
        fieldName: 'endedAt',
        columnName: 'Ende',
        columnWidth: '160px',
        renderItem: (s: CallSession) => formatTime(s.endedAt),
      },
      {
        customID: 'duration',
        columnName: 'Dauer',
        columnWidth: '100px',
        renderItem: (s: CallSession) => {
          if (!s.startedAt || !s.endedAt) return '-';
          const duration = intervalToDuration({ start: new Date(s.startedAt), end: new Date(s.endedAt) });
          if (!duration) return '-';
          const hours = (duration.hours ?? 0).toString().padStart(2, '0');
          const minutes = (duration.minutes ?? 0).toString().padStart(2, '0');
          const seconds = (duration.seconds ?? 0).toString().padStart(2, '0');
          return `${hours}:${minutes}:${seconds}`;
        },
      },
      {
        customID: 'origin',
        columnName: 'Herkunft',
        columnWidth: '110px',
        renderItem: (s: CallSession) => {
          if (!s.callOrigin) return '-';
          return s.callOrigin === 'phone'
            ? html`<span class="badge badge-primary badge-sm">Twilio</span>`
            : html`<span class="badge badge-ghost badge-sm">Browser</span>`;
        },
      },
      {
        customID: 'phones',
        columnName: 'Telefonnummern',
        columnWidth: '250px',
        renderItem: (s: CallSession) => {
          const carrier = s.carrierPhoneNumber;
          const spoken = s.spokenPhoneNumber;
          if (!carrier && !spoken) return '-';
          return html`<div class="flex flex-col gap-0.5 text-xs leading-tight">
            ${carrier ? html`<span><span class="opacity-60">Twilio:</span> ${carrier}</span>` : nothing}
            ${spoken ? html`<span><span class="opacity-60">genannt:</span> ${spoken}</span>` : nothing}
          </div>`;
        },
      },
      {
        customID: '_options',
        columnWidth: '300px',
        renderItem: (s: CallSession) =>
          html`<div class="flex items-center gap-3">
            <a
              class="btn btn-sm"
              style="background:#2563eb;border-color:#2563eb;color:#fff"
              href=${`/calls/${s.sessionID}`}
              >Details</a
            >
            ${CallResultBadge({ callID: s.sessionID ?? '' })}
          </div>`,
      },
      {
        customID: 'success',
        columnName: 'Success',
        columnWidth: '200px',
        renderItem: (s: CallSession) => CallSuccessSelect({ callID: s.sessionID ?? '', store: successStore }),
      },
    ],
    // Dummy view to satisfy DataTable, searchable by call ID (sessionID === callID)
    view: new View({
      filter: new AdornisFilter({
        search: searchQuery || null,
        searchFields: ['sessionID'],
      }),
    }),
  });

  return html`
    <div class="card bg-base-100 m-4">
      <div class="card-body p-8">
        <div class="flex flex-row items-center gap-4 mb-6 flex-wrap">
          <h2 class="card-title text-2xl font-bold text-primary">Anruf Übersicht</h2>
          <div class="flex items-center">
            <label class="input input-bordered input-sm flex items-center gap-2 w-72">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 opacity-70 flex-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                />
              </svg>
              <input
                type="text"
                class="grow"
                placeholder="Search calls…"
                @input=${(e: any) => setSearchQuery(e.target.value)}
              />
            </label>
          </div>
          <label class="flex items-center gap-2 text-sm">
            Zeitraum
            <select
              class="select select-bordered select-sm"
              aria-label="Zeitraum"
              .value=${period}
              @change=${(event: Event) => setPeriod((event.target as HTMLSelectElement).value as SuccessPeriod)}
            >
              ${SUCCESS_PERIODS.map(option => html`<option value=${option.value}>${option.label}</option>`)}
            </select>
          </label>
          <button class="btn btn-sm btn-outline" @click=${() => setReloadCount(reloadCount + 1)}>Aktualisieren</button>
        </div>
        ${CallSuccessSummary({ store: successStore, search: searchQuery, period, refresh: reloadCount })}
        ${DataTable({ options: dataViewOptions.dataViewOptions })}
      </div>
    </div>
  `;
});
